import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  HStack,
  Radio,
  RadioGroup,
  Spinner,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useBankAccountsModel } from '@common/models/BankAccountsModel';
import { BankDetailStatus } from '@services/appsync/types';
import React, { useState } from 'react';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import PlaidLink from 'src/apps/invest/common/components/PlaidLink';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { VerifyAccountButton } from 'src/apps/invest/common/components/VerifyAccountButton';

export const PaymentSourceSelector: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const bankAccountsModel = useBankAccountsModel();
  const toast = useToast();

  async function handleDefaultPaymentChange(bankId: string) {
    setIsLoading(true);
    const result = await bankAccountsModel.setDefaultBankAccount(bankId);
    if (!result.success) {
      toast({
        description: result.error + '. Please try again or contact support if the problem persists.',
        duration: 5000,
        isClosable: true,
        position: 'top',
        status: 'error',
        title: 'Error',
      });
    }
    setIsLoading(false);
  }

  async function handlePlaidLinkSuccess(token: string, metadata: PlaidLinkOnSuccessMetadata) {
    setIsLoading(true);
    const status = await bankAccountsModel.addBankAccount(token, metadata);
    if (!status.success) alert(status.error);
    setIsLoading(false);
  }

  if (isLoading)
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertTitle>Updating Payment Information...</AlertTitle>
        <Spinner alignSelf={'flex-end'} />
      </Alert>
    );

  return (
    <VStack alignItems={'flex-start'} justifyContent={'flex-start'} gap={'30px'}>
      <VStack width="full" borderRadius="10px" borderWidth={'1px'} borderColor="blackAlpha.200">
        <RadioGroup width="100%" value={bankAccountsModel.getDefaultBankAccount()?.id as string}>
          {bankAccountsModel.bankAccounts.map((bankAccount, i) => (
            <HStack
              key={bankAccount.id}
              borderBottomWidth={i === bankAccountsModel.bankAccounts.length - 1 ? 0 : '1px'}
              borderColor="blackAlpha.200"
              cursor={bankAccount.status === BankDetailStatus.VERIFIED ? 'pointer' : 'default'}
              gap={['10px', '25px']}
              justifyContent={['flex-start', 'space-between']}
              onClick={
                bankAccount.status === BankDetailStatus.VERIFIED
                  ? () => handleDefaultPaymentChange(bankAccount.id as string)
                  : undefined
              }
              opacity={bankAccount.status === BankDetailStatus.VERIFIED ? 1 : 0.5}
              padding={['10px', '28px']}
              width="full">
              <HStack order={[2, 1]} gap={['5px']}>
                <Icon display={['none', 'flex']} icon="credit-card" boxSize={'27px'} color="blue.500" />
                <TextBlock weight="bold">{bankAccount.name}</TextBlock>
                <TextBlock as={'span'} color="blackAlpha.400">
                  {bankAccount.status === BankDetailStatus.VERIFIED
                    ? `(...${bankAccount.account_number || ''})`
                    : 'Needs Verification'}
                </TextBlock>
              </HStack>
              {bankAccount.status === BankDetailStatus.VERIFIED && (
                <Radio
                  order={[1, 2]}
                  value={bankAccount.id as string}
                  isDisabled={bankAccount.status !== BankDetailStatus.VERIFIED}
                />
              )}
              {bankAccount.status !== BankDetailStatus.VERIFIED && bankAccount.id && (
                <Box order={[1, 2]}>
                  <VerifyAccountButton accountID={bankAccount.id} />
                </Box>
              )}
            </HStack>
          ))}
        </RadioGroup>
      </VStack>
      <PlaidLink
        onSuccess={handlePlaidLinkSuccess}
        buttonProps={{
          children: 'Add another payment method',
          gap: '10px',
          leftIcon: <Icon icon="plus" />,
          variant: 'link',
        }}
      />
    </VStack>
  );
};

export default PaymentSourceSelector;